import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import LeadFormWizard from "@/components/landing/LeadFormWizard";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { SITE_URL } from "@/data/servicePages";

const OFFICES = [
  { city: "Dubai", detail: "Gate Village, DIFC" },
  { city: "Zurich", detail: "Bahnhofstrasse" },
  { city: "Singapore", detail: "Raffles Place" },
  { city: "London", detail: "Mayfair" },
  { city: "New York", detail: "Park Avenue" },
];

export default function ContactPage() {
  useSEO({
    title: "Contact Aurelius Family Office - Private Consultation",
    description:
      "Speak with a senior family-office partner. Confidential, no-obligation consultations for HNW and UHNW families across Dubai (DIFC), Zurich, Singapore, London and New York.",
    keywords: "contact family office, family office consultation, family office advisor contact, private wealth consultation",
    canonical: `${SITE_URL}/contact`,
    ogTitle: "Contact Aurelius Family Office",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Aurelius Family Office",
        url: `${SITE_URL}/contact`,
      },
      {
        "@context": "https://schema.org",
        "@type": "FinancialService",
        name: "Aurelius Family Office",
        url: `${SITE_URL}/`,
        email: "enquiries@aureliusfamilyoffice.com",
        telephone: "+971 4 000 0000",
        areaServed: ["Dubai", "Switzerland", "Singapore", "London", "United States"],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "sales",
          email: "enquiries@aureliusfamilyoffice.com",
          telephone: "+971 4 000 0000",
          availableLanguage: ["English", "Arabic"],
        },
      },
    ],
  });
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div className="bg-paper">
      <Navbar />

      {/* Emerald hero */}
      <section className="bg-brand text-white">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 lg:px-12 pt-36 pb-20">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> Aurelius Family Office
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-200 mb-4">Contact</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.98] max-w-3xl">
            Let us talk about your structure.
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            Every engagement begins with a confidential, no-obligation conversation with a senior partner. Tell us where your family is today, and we will tell you honestly whether we can help.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
            <a href="mailto:enquiries@aureliusfamilyoffice.com" className="group flex items-center gap-4 border border-white/15 rounded-2xl p-5 hover:bg-white/5 transition-colors">
              <span className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0"><Mail className="w-5 h-5" /></span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-white/50">Email</span>
                <span className="block text-sm font-medium mt-0.5">enquiries@aureliusfamilyoffice.com</span>
              </span>
            </a>
            <a href="tel:+97140000000" className="group flex items-center gap-4 border border-white/15 rounded-2xl p-5 hover:bg-white/5 transition-colors">
              <span className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0"><Phone className="w-5 h-5" /></span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-white/50">Telephone</span>
                <span className="block text-sm font-medium mt-0.5">+971 4 000 0000</span>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="bg-paper py-20 md:py-24 border-b border-mist">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10 lg:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-10">Offices</p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-5 border-t border-s border-mist">
            {OFFICES.map((o, i) => (
              <Reveal key={o.city} delay={i * 0.06}>
                <div className="border-b border-e border-mist p-6 lg:p-8 h-full">
                  <MapPin className="w-5 h-5 text-brand mb-4" />
                  <h3 className="font-display font-semibold text-lg text-ink">{o.city}</h3>
                  <p className="text-sm text-ink/55 mt-1">{o.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The consultation form */}
      <LeadFormWizard />
      <Footer />
    </div>
  );
}
